import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { clientContactsApi, type ClientContactDto } from "@/lib/client-contacts";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface ClientContactPickerProps {
  clientId: number | null | undefined;
  value: number | null;
  onChange: (contactId: number | null) => void;
  placeholder?: string;
}

function displayName(c: ClientContactDto): string {
  return c.lastName ? `${c.firstName} ${c.lastName}` : c.firstName;
}

export function ClientContactPicker({
  clientId,
  value,
  onChange,
  placeholder,
}: ClientContactPickerProps) {
  const [open, setOpen] = useState(false);
  const disabled = clientId == null;

  const { data } = useQuery({
    queryKey: ["client-contacts", clientId],
    queryFn: () => clientContactsApi.list(clientId!),
    enabled: !disabled,
    staleTime: 5000,
  });

  const selectedContact = data?.find((c) => c.id === value);

  if (disabled) {
    return (
      <div
        className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 text-sm text-muted-foreground"
        aria-disabled="true"
      >
        {placeholder ?? "Select a client first"}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 px-3 font-normal"
        >
          {selectedContact ? (
            <span className="truncate">{displayName(selectedContact)}</span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder ?? "Select contact..."}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0" 
        style={{ width: "var(--radix-popover-trigger-width)" }}
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search contact..." />
          <CommandList>
            <CommandEmpty>No contact found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check className={cn("mr-2 h-4 w-4", value === null ? "opacity-100" : "opacity-0")} />
                None
              </CommandItem>
              {data?.map((c) => (
                <CommandItem
                  key={c.id}
                  value={displayName(c)}
                  onSelect={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                  <span>{displayName(c)}</span>
                  {c.email && <span className="ml-2 text-muted-foreground truncate">({c.email})</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
